'use client'

import { useState } from 'react'
import { useMutation, useQuery } from '@tanstack/react-query'
import { CreateFileParams, FilesFilterParams } from '@/types/file'
import { useFilesStore } from '@/store'
import { useBulkDeleteSuccessHandler } from '@/hooks/use-bulk-delete'

/**
 * Handles smooth progress animation from 90% to 98% during backend processing
 * @param setProgress - Function to update progress state
 * @returns Cleanup function to clear the interval
 */
const startProcessingAnimation = (setProgress: (progress: number) => void): (() => void) => {
  let currentProgress = 90
  const interval = setInterval(() => {
    currentProgress += 0.5 // Increment by 0.5% every 200ms
    if (currentProgress <= 98) {
      setProgress(Math.round(currentProgress))
    } else {
      clearInterval(interval)
    }
  }, 200) // Update every 200ms for smooth animation

  return () => clearInterval(interval)
}

/**
 * Wraps a progress callback to automatically trigger processing animation at 90%
 * @param setProgress - Function to update progress state
 * @param onProgress - Optional original progress callback
 * @returns Object with wrapped callback and cleanup function
 */
const createProgressHandler = (setProgress: (progress: number) => void, onProgress?: (progress: number) => void) => {
  let hasReached90 = false
  let cleanup: (() => void) | null = null

  const handleProgress = (progress: number) => {
    setProgress(progress)

    // When upload reaches 90%, start simulated processing progress
    if (progress >= 90 && !hasReached90) {
      hasReached90 = true
      cleanup = startProcessingAnimation(setProgress)
    }

    // Call the original callback if provided
    onProgress?.(progress)
  }

  const cleanupInterval = () => {
    if (cleanup) {
      cleanup()
    }
  }

  return { handleProgress, cleanupInterval }
}

export const useFiles = (params: FilesFilterParams) => {
  const fetchFiles = useFilesStore(state => state.fetchFiles)

  return useQuery({
    queryKey: ['files', params],
    queryFn: () => fetchFiles(params),
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000
  })
}

export const useFile = (id: string) => {
  const getFile = useFilesStore(state => state.getFile)

  return useQuery({
    queryKey: ['file', id],
    queryFn: () => getFile(id),
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    enabled: !!id
  })
}

export const useDownloadFile = () => {
  const addToDownloadQueue = useFilesStore(state => state.addToDownloadQueue)
  const cancelDownload = useFilesStore(state => state.cancelDownload)
  const removeFromDownloadQueue = useFilesStore(state => state.removeFromDownloadQueue)
  const clearDownloads = useFilesStore(state => state.clearDownloads)
  const getFile = useFilesStore(state => state.getFile)
  const downloads = useFilesStore(state => state.downloads)

  const mutation = useMutation({
    mutationFn: async (id: string) => {
      // With our new structure, we don't need to check if the file is already in the queue
      // because we want to allow multiple downloads of the same file
      try {
        const file = await getFile(id)
        addToDownloadQueue(file)
      } catch (error) {
        throw error
      }

      return id
    }
  })

  return {
    ...mutation,
    downloads: downloads.items,
    cancelDownload,
    removeFromDownloadQueue,
    clearDownloads
  }
}

export const useCreateFile = () => {
  const createFile = useFilesStore(state => state.createFile)
  const [uploadProgress, setUploadProgress] = useState<number>(0)

  const mutation = useMutation({
    mutationFn: async (params: CreateFileParams) => {
      setUploadProgress(0)

      const { handleProgress, cleanupInterval } = createProgressHandler(setUploadProgress, params.onProgress)

      try {
        const result = await createFile({
          ...params,
          onProgress: handleProgress
        })
        cleanupInterval()
        return result
      } catch (error) {
        cleanupInterval()
        throw error
      }
    },
    onSuccess: () => {
      setUploadProgress(100)
    },
    onError: () => {
      setUploadProgress(0)
    }
  })

  return {
    ...mutation,
    uploadProgress
  }
}

export const useUpdateFile = () => {
  const updateFile = useFilesStore(state => state.updateFile)
  const [uploadProgress, setUploadProgress] = useState<number>(0)

  const mutation = useMutation({
    mutationFn: async ({
      id,
      params
    }: {
      id: string
      params: { name: string; file?: File; optimize?: boolean; accessLevel?: import('@/enums').AccessLevelEnum }
    }) => {
      setUploadProgress(0)

      const formData = new FormData()
      formData.append('name', params.name)

      if (params.file) {
        formData.append('file', params.file)
      }

      if (params.optimize !== undefined) {
        formData.append('optimize', params.optimize.toString())
      }

      if (params.accessLevel !== undefined) {
        formData.append('accessLevel', params.accessLevel)
      }

      const { handleProgress, cleanupInterval } = createProgressHandler(setUploadProgress)

      try {
        const result = await updateFile(id, formData, handleProgress)
        cleanupInterval()
        return result
      } catch (error) {
        cleanupInterval()
        throw error
      }
    },
    onSuccess: () => {
      setUploadProgress(100)
    },
    onError: () => {
      setUploadProgress(0)
    }
  })

  return {
    ...mutation,
    uploadProgress
  }
}

export const useDeleteFile = () => {
  const deleteFile = useFilesStore(state => state.deleteFile)

  return useMutation({
    mutationFn: (id: string) => deleteFile(id),
    onSuccess: () => {}
  })
}

export const useBulkDeleteFiles = () => {
  const bulkDeleteFiles = useFilesStore(state => state.bulkDeleteFiles)
  const deselectAllFiles = useFilesStore(state => state.deselectAllFiles)
  const handleBulkDeleteSuccess = useBulkDeleteSuccessHandler()

  return useMutation({
    mutationFn: (ids: string[]) => bulkDeleteFiles(ids),
    onSuccess: response => {
      deselectAllFiles()
      handleBulkDeleteSuccess(response)
    }
  })
}
